

import * as React from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import type { NavigateOptions } from 'react-router-dom';
import { useAppContext } from './state/AppContext';
import { useAuth } from './state/AuthContext';
import type { Tab } from '@/shared/types';
import { UserProfile } from '@/shared/components/UserProfile';
import { MenuIcon, XIcon, SpinnerIcon } from '@/shared/components/common/Icons';
import { Chatbot } from '@/shared/components/Chatbot';
import { SiteFooter } from '@/shared/components/SiteFooter';
import { DebugModeToggle } from '@/shared/components/common/DebugModeToggle';
import { usePageTracking } from '@/shared/hooks/usePageTracking';
import { useScrollDepth } from '@/shared/hooks/useScrollDepth';
import { useKeyboardShortcuts, createGlobalShortcuts, type ShortcutMap } from '@/shared/hooks/useKeyboardShortcuts';
import { KeyboardShortcutsModal } from '@/shared/components/KeyboardShortcutsModal';
import { trackCTAClick, trackTabChange } from '@/services/analyticsService';
import { FeatureErrorBoundary } from '@/shared/components/common/ErrorBoundary';
import { Analytics } from '@vercel/analytics/react';
import { SpeedInsights } from '@vercel/speed-insights/react';

const OptimizerView = React.lazy(() => import('@/features/optimizer/pages/OptimizerView').then(m => ({ default: m.OptimizerView })));
const Methodology = React.lazy(() => import('@/features/info/pages/Methodology').then(m => ({ default: m.Methodology })));
const Glossary = React.lazy(() => import('@/features/info/pages/Glossary').then(m => ({ default: m.Glossary })));
const Faq = React.lazy(() => import('@/features/info/pages/Faq').then(m => ({ default: m.Faq })));
const PerformanceVerification = React.lazy(() => import('@/features/verification/pages/PerformanceVerification').then(m => ({ default: m.PerformanceVerification })));
// Advertising Health (renamed from Portfolio Health)
const AdvertisingHealth = React.lazy(() => import('@/features/portfolio/pages/PortfolioHealth').then(m => ({ default: m.PortfolioHealth })));
const Login = React.lazy(() => import('@/features/auth/pages/Login').then(m => ({ default: m.Login })));
const SignUp = React.lazy(() => import('@/features/auth/pages/SignUp').then(m => ({ default: m.SignUp })));
const NotFound = React.lazy(() => import('@/app/pages/NotFound').then(m => ({ default: m.NotFound })));
const LandingPage = React.lazy(() => import('@/app/pages/LandingPage').then(m => ({ default: m.LandingPage })));
const Checkout = React.lazy(() => import('@/features/subscription/pages/Checkout').then(m => ({ default: m.Checkout })));
const CheckoutSuccess = React.lazy(() => import('@/features/subscription/pages/Checkout').then(m => ({ default: m.CheckoutSuccess })));
const CheckoutCanceled = React.lazy(() => import('@/features/subscription/pages/Checkout').then(m => ({ default: m.CheckoutCanceled })));
const SubscriptionManagement = React.lazy(() => import('@/features/subscription/pages/SubscriptionManagement').then(m => ({ default: m.SubscriptionManagement })));
const ProfilePage = React.lazy(() => import('@/features/profile/pages/ProfilePage').then(m => ({ default: m.ProfilePage })));
const PricingPage = React.lazy(() => import('@/features/pricing/pages/PricingPage').then(m => ({ default: m.PricingPage })));
const UsageTierCheckout = React.lazy(() => import('@/features/pricing/pages/UsageTierCheckout').then(m => ({ default: m.default })));
const UsageTierSuccess = React.lazy(() => import('@/features/pricing/pages/UsageTierCheckout').then(m => ({ default: m.UsageTierSuccess })));
const InventoryIntelligence = React.lazy(() =>
  import('@/features/inventory/pages/InventoryIntelligence').then(m => ({ default: m.InventoryIntelligence }))
);
const InventoryLandingPage = React.lazy(() => import('@/app/pages/InventoryLandingPage'));
// Pricing Optimizer (Analytics Extension)
const PricingAnalysis = React.lazy(() => import('@/features/pricing/pages/PricingAnalysis').then(m => ({ default: m.PricingAnalysis })));
// P&L Reporting (Analytics Extension)
const PLReporting = React.lazy(() => import('@/features/reporting/pages/PLReporting').then(m => ({ default: m.PLReporting })));
// Promotions Optimizer (Analytics Extension)
const PromotionsAnalysis = React.lazy(() => import('@/features/promotions/pages/PromotionsAnalysis').then(m => ({ default: m.PromotionsAnalysis })));
// Coming Soon pages for features in development
const CatalogHealthComingSoon = React.lazy(() => import('@/app/pages/ComingSoon').then(m => ({ default: m.CatalogHealthComingSoon })));
// Help Center (Analytics Extension)
const HelpCenter = React.lazy(() => import('@/features/help/pages/HelpCenter'));
// Admin Dashboard (Admin Only)
const AdminDashboard = React.lazy(() => import('@/features/admin/pages/AdminDashboard'));

const TAB_PATHS: Record<Tab, string> = {
  // Optimizers
  optimizer: '/optimizer',
  inventory: '/inventory',
  'pricing-optimizer': '/pricing-optimizer',
  promotions: '/promotions',
  // Health Pages
  'advertising-health': '/advertising-health',
  'catalog-health': '/catalog-health',
  'inventory-health': '/inventory-health',
  // Suites
  verification: '/verification',
  reporting: '/reporting',
  // Info
  methodology: '/methodology',
  glossary: '/glossary',
  faq: '/faq',
};

const PATH_TO_TAB: Record<string, Tab> = Object.entries(TAB_PATHS).reduce((acc, [tab, path]) => {
  acc[path] = tab as Tab;
  return acc;
}, {} as Record<string, Tab>);

export default function App() {
  const { state, dispatch } = useAppContext();
  const { activeTab, analysisResults } = state;
  const { user, isLoading: authLoading, logOut } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [isShortcutsModalOpen, setIsShortcutsModalOpen] = React.useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const inventoryLandingRoute = '/inventory/overview';

  // Analytics tracking hooks
  usePageTracking();
  useScrollDepth();

  // Navigation paths for keyboard shortcuts (ordered for 1-5 number keys)
  const shortcutNavPaths = [
    TAB_PATHS.optimizer,           // 1 - Ad Optimizer
    TAB_PATHS['advertising-health'], // 2 - Ad Health
    TAB_PATHS.inventory,           // 3 - Inventory
    TAB_PATHS['pricing-optimizer'], // 4 - Pricing
    TAB_PATHS.promotions,          // 5 - Promotions
  ];

  // Create keyboard shortcuts
  const shortcuts: ShortcutMap = React.useMemo(() => {
    return createGlobalShortcuts({
      onShowHelp: () => setIsShortcutsModalOpen(true),
      onSearch: () => {
        // Focus the chatbot input if available, or navigate to help
        const chatbotTrigger = document.querySelector('[aria-label="Open chatbot"]') as HTMLButtonElement;
        if (chatbotTrigger) {
          chatbotTrigger.click();
        }
      },
      onSave: () => {
        // Trigger save in the current context - dispatch a custom event
        window.dispatchEvent(new CustomEvent('keyboard-save'));
      },
      onEscape: () => {
        // Close modals in order of priority
        if (isShortcutsModalOpen) {
          setIsShortcutsModalOpen(false);
          return;
        }
        if (isMobileMenuOpen) {
          setIsMobileMenuOpen(false);
          return;
        }
        // Dispatch escape event for other components
        window.dispatchEvent(new CustomEvent('keyboard-escape'));
      },
      onNavigateTab: (index: number) => {
        if (index >= 1 && index <= shortcutNavPaths.length) {
          navigate(shortcutNavPaths[index - 1]);
        }
      },
    });
  }, [navigate, isShortcutsModalOpen, isMobileMenuOpen]);

  // Register keyboard shortcuts
  useKeyboardShortcuts(shortcuts, !authLoading);

  React.useEffect(() => {
    const currentTab = PATH_TO_TAB[location.pathname] || 'optimizer';
    if (activeTab !== currentTab) {
      dispatch({ type: 'SET_ACTIVE_TAB', payload: currentTab });
      trackTabChange(currentTab);
    }
  }, [activeTab, dispatch, location.pathname]);

  const handleLogin = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      trackCTAClick('login');
      setIsMobileMenuOpen(false);
      navigate('/login');
    },
    [navigate]
  );

  const handleSignup = React.useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      trackCTAClick('sign_up');
      setIsMobileMenuOpen(false);
      navigate('/signup');
    },
    [navigate]
  );

  const handleLogout = React.useCallback(async () => {
    setIsMobileMenuOpen(false);
    await logOut();
  }, [logOut]);

  const navigateToTab = React.useCallback(
    (tab: Tab, options?: NavigateOptions) => {
      const path = TAB_PATHS[tab];
      if (!path) return;
      navigate(path, options);
      setIsMobileMenuOpen(false);
    },
    [navigate]
  );

  React.useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const openInfoPage = (title: string, content: string) => {
    const newWindow = window.open('', '_blank');
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html lang="en">
        <head>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <title>${title}</title>
          <style>
            body { font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif; line-height: 1.6; color: #333; background-color: #f8f9fa; margin: 0; padding: 2rem; }
            .container { max-width: 800px; margin: 0 auto; background-color: #ffffff; padding: 2rem 3rem; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.08); }
            h1 { color: #111; border-bottom: 2px solid #f1f3f5; padding-bottom: 0.5rem; }
            h2 { color: #222; margin-top: 2.5rem; }
            p, li { color: #555; }
            ul { padding-left: 20px; }
          </style>
        </head>
        <body>
          <div class="container">
            <h1>${title}</h1>
            ${content}
          </div>
        </body>
        </html>
      `);
      newWindow.document.close();
    }
  };

  const privacyPolicyContent = `
    <p><strong>Effective Date:</strong> ${new Date().toLocaleDateString()}</p>
    <h2>1. Introduction</h2>
    <p>Welcome to Amazon Profit Studio. We are committed to protecting your privacy. This Privacy Policy explains how we handle your information when you use our application.</p>
    <h2>2. Information We Do Not Collect</h2>
    <p>This is a client-side application. This means all data processing occurs locally in your web browser. We do not collect, store, transmit, or have access to any of your personal information or your Amazon advertising data.</p>
    <ul>
      <li>Your uploaded CSV files are processed locally and are never sent to any server.</li>
      <li>We do not use cookies to track you.</li>
      <li>We do not collect personal information like your name, email address, or IP address.</li>
    </ul>
    <h2>3. Information Processed by Third Parties</h2>
    <p>The "AI-Powered Summary" feature uses the Google Gemini API to generate text. When you use this feature, the pre-processed, anonymized summary data (which does not contain your specific ASINs, sales figures, or spend) is sent to Google's servers for processing. We recommend reviewing the <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a> for more information on how they handle data.</p>
    <h2>4. Your Data Security</h2>
    <p>Since your data remains in your browser, you are in control of its security. We recommend using the application on a secure computer and network.</p>
    <h2>5. Changes to This Privacy Policy</h2>
    <p>We may update this Privacy Policy from time to time. Any changes will be posted on this page.</p>
    <h2>6. Contact Us</h2>
    <p>If you have any questions about this Privacy Policy, please feel free to reach out via the developer's LinkedIn profile provided in the footer.</p>
  `;

  const termsOfServiceContent = `
    <p><strong>Last Updated:</strong> ${new Date().toLocaleDateString()}</p>
    <h2>1. Acceptance of Terms</h2>
    <p>By accessing or using Amazon Profit Studio ("Service"), you agree to be bound by these Terms of Service ("Terms"). If you disagree with any part of the terms, then you may not access the Service.</p>
    <h2>2. Description of Service</h2>
    <p>The Service provides tools to analyze Amazon advertising data, inventory, pricing, and business performance. All data processing is performed on the client-side within your browser. No data is stored or transmitted to our servers.</p>
    <h2>3. Disclaimer of Warranties</h2>
    <p>The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The use of the Service is at your sole risk. The Service is provided without warranties of any kind, whether express or implied, including, but not to, implied warranties of merchantability, fitness for a particular purpose, non-infringement, or course of performance.</p>
    <p>The analysis, recommendations, and projections provided by the Service are for informational purposes only and are not guaranteed to be accurate or to result in any specific profit outcome. You are solely responsible for any actions you take based on the information provided by the Service.</p>
    <h2>4. Limitation of Liability</h2>
    <p>In no event shall the developer of the Service be liable for any indirect, incidental, special, consequential or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; and (iii) any decisions made or actions taken in reliance upon the information provided through the Service.</p>
    <h2>5. Governing Law</h2>
    <p>These Terms shall be governed and construed in accordance with the laws of the jurisdiction in which the developer resides, without regard to its conflict of law provisions.</p>
  `;

  const cookiesContent = `
    <p><strong>Last Updated:</strong> ${new Date().toLocaleDateString()}</p>
    <h2>What Are Cookies?</h2>
    <p>Cookies are small text files stored on your device (computer, tablet, mobile phone) when you visit certain websites. They are used to 'remember' you and your preferences, either for a single visit (through a 'session cookie') or for multiple repeat visits (using a 'persistent cookie').</p>
    <h2>Our Use of Cookies</h2>
    <p>Amazon Profit Studio operates entirely on the client-side. This means all the application logic and data processing happens directly in your web browser.</p>
    <p><strong>We do not use cookies.</strong></p>
    <p>We do not need to store your preferences between sessions, track your activity, or identify you. Your session is contained within your browser tab. When you close the tab, the application state is reset. This simple, privacy-focused approach means we have no need for cookies of any kind.</p>
    <h2>Third-Party Services</h2>
    <p>The application does not embed third-party services that would set their own cookies.</p>
    <h2>Your Control</h2>
    <p>As we do not use cookies, there are no settings for you to manage on this site. You can, however, manage cookies at a browser level. Please consult your browser's help documentation for information on how to block or delete cookies.</p>
  `;

  const isOptimizerRoute = location.pathname === TAB_PATHS.optimizer;
  const handleAuthSuccess = React.useCallback(() => {
    navigateToTab('optimizer', { replace: true });
  }, [navigateToTab]);

  const renderIfGuest = (element: React.ReactElement) =>
    user ? <Navigate to={TAB_PATHS.optimizer} replace /> : element;

  // Grouped navigation structure
  const navGroups = [
    {
      label: 'Optimize',
      items: [
        { label: 'Ad Spend', path: TAB_PATHS.optimizer },
        { label: 'Inventory', path: TAB_PATHS.inventory },
        { label: 'Pricing', path: TAB_PATHS['pricing-optimizer'] },
        { label: 'Promotions', path: TAB_PATHS.promotions },
      ],
    },
    {
      label: 'Monitor',
      items: [
        { label: 'Advertising Health', path: TAB_PATHS['advertising-health'] },
        { label: 'Catalog Health', path: TAB_PATHS['catalog-health'], badge: 'Soon' },
        { label: 'Inventory Health', path: TAB_PATHS['inventory-health'] },
      ],
    },
    {
      label: 'Analyze',
      items: [
        { label: 'Ad Results', path: TAB_PATHS.verification },
        { label: 'Reporting', path: TAB_PATHS.reporting },
      ],
    },
  ];

  // Main navigation links
  // Order: Core workflow → Analytics → Coming Soon features
  const headerNavLinks: { label: string; path: string; badge?: string }[] = [
    { label: 'Ad Optimizer', path: TAB_PATHS.optimizer },
    { label: 'Ad Health', path: TAB_PATHS['advertising-health'] },
    { label: 'Ad Results', path: TAB_PATHS.verification },
    { label: 'Inventory', path: TAB_PATHS.inventory },
    { label: 'Pricing', path: TAB_PATHS['pricing-optimizer'] },
    { label: 'Promotions', path: TAB_PATHS.promotions },
    { label: 'Catalog', path: TAB_PATHS['catalog-health'], badge: 'Soon' },
    { label: 'Reporting', path: TAB_PATHS.reporting },
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-100 dark:bg-slate-900">
        <div className="flex items-center gap-3 text-slate-600 dark:text-slate-300">
          <SpinnerIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
          <span className="font-medium">Checking your session...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:top-4 focus:left-4 focus:px-4 focus:py-2 focus:bg-blue-600 focus:text-white focus:rounded-md focus:font-semibold focus:shadow-lg"
      >
        Skip to main content
      </a>
      <header className="bg-white/80 dark:bg-slate-900/85 backdrop-blur-sm shadow-sm border-b border-slate-200 dark:border-slate-700 sticky top-0 z-40 transition-colors duration-300">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <a
            href={user ? TAB_PATHS.optimizer : '/'}
            onClick={e => {
              e.preventDefault();
              if (user) {
                navigateToTab('optimizer');
              } else {
                navigate('/');
              }
            }}
            className="focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded-md"
          >
            <h1 className="text-lg sm:text-2xl font-semibold tracking-tight font-display text-transparent bg-clip-text bg-gradient-to-r from-slate-900 via-indigo-900 to-slate-700 dark:from-slate-100 dark:via-indigo-300 dark:to-slate-300">
              Amazon Profit Studio
            </h1>
          </a>
          <div className="flex items-center gap-4">
            <nav className="hidden lg:flex items-center gap-2">
              {headerNavLinks.map(link => (
                <NavLink
                  key={link.label}
                  to={link.path}
                  className={({ isActive }) =>
                    `px-3 py-1.5 rounded-full text-sm font-medium transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 flex items-center gap-1.5 ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                        : 'text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100/80 dark:hover:bg-slate-800/80'
                    }`
                  }
                >
                  {link.label}
                  {link.badge && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                      {link.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="hidden lg:flex items-center gap-4 ml-4 pl-4 border-l border-slate-200 dark:border-slate-700">
              <UserProfile user={user} onLogin={handleLogin} onSignup={handleSignup} onLogout={handleLogout} />
            </div>
            <div className="lg:hidden flex items-center gap-2">
              <button
                onClick={() => setIsMobileMenuOpen(true)}
                className="p-2.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-500 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Open main menu"
              >
                <MenuIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </header>
      <main id="main-content" className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full flex-grow">
        <FeatureErrorBoundary feature="Page">
          <React.Suspense fallback={<div className="p-6 text-center text-slate-600 dark:text-slate-400">Loading...</div>}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            {/* Optimizers */}
            <Route path={TAB_PATHS.optimizer} element={<OptimizerView />} />
            <Route path={TAB_PATHS.inventory} element={<InventoryIntelligence />} />
            <Route path={TAB_PATHS['pricing-optimizer']} element={<PricingAnalysis />} />
            <Route path={TAB_PATHS.promotions} element={<PromotionsAnalysis />} />
            {/* Health Pages */}
            <Route path={TAB_PATHS['advertising-health']} element={<AdvertisingHealth />} />
            <Route path={TAB_PATHS['catalog-health']} element={<CatalogHealthComingSoon />} />
            <Route path={TAB_PATHS['inventory-health']} element={<InventoryIntelligence />} />
            {/* Suites */}
            <Route path={TAB_PATHS.verification} element={<PerformanceVerification />} />
            <Route path={TAB_PATHS.reporting} element={<PLReporting />} />
            {/* Info */}
            <Route path={TAB_PATHS.methodology} element={<Methodology />} />
            <Route path={TAB_PATHS.glossary} element={<Glossary />} />
            <Route path={TAB_PATHS.faq} element={<Faq />} />
            {/* Help Center */}
            <Route path="/help" element={<HelpCenter />} />
            <Route path={inventoryLandingRoute} element={<InventoryLandingPage />} />
            {/* Legacy route redirect */}
            <Route path="/portfolio-health" element={<Navigate to={TAB_PATHS['advertising-health']} replace />} />
            {/* Admin Dashboard (Admin Only) */}
            <Route path="/admin" element={<AdminDashboard />} />
            {/* Auth */}
            <Route path="/login" element={renderIfGuest(<Login onAuthenticated={handleAuthSuccess} />)} />
            <Route path="/signup" element={renderIfGuest(<SignUp onAuthenticated={handleAuthSuccess} />)} />
            <Route path="/checkout" element={<Checkout />} />
            <Route path="/checkout/success" element={<CheckoutSuccess />} />
            <Route path="/checkout/canceled" element={<CheckoutCanceled />} />
            <Route path="/settings/profile" element={<ProfilePage />} />
            <Route path="/settings/subscription" element={<SubscriptionManagement />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/pricing/checkout" element={<UsageTierCheckout />} />
            <Route path="/pricing/success" element={<UsageTierSuccess />} />
            <Route path="*" element={<NotFound />} />
            </Routes>
          </React.Suspense>
        </FeatureErrorBoundary>
      </main>

      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex" role="dialog" aria-modal="true">
          <div
            className="fixed inset-0 bg-black/50 dark:bg-black/70 transition-opacity"
            onClick={() => setIsMobileMenuOpen(false)}
            aria-hidden="true"
          ></div>
          <div className="relative bg-white dark:bg-slate-800 w-full max-w-xs h-full p-6 flex flex-col shadow-xl">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Menu</h2>
              <button
                onClick={() => setIsMobileMenuOpen(false)}
                className="p-2.5 -mr-2.5 rounded-md text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                aria-label="Close menu"
              >
                <XIcon className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-1">
              {headerNavLinks.map(link => (
                <NavLink
                  key={link.label}
                  to={link.path}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center justify-between ${
                      isActive
                        ? 'text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30'
                        : 'text-slate-700 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-slate-100 dark:hover:bg-slate-700'
                    }`
                  }
                >
                  {link.label}
                  {link.badge && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 dark:bg-amber-900/50 text-amber-700 dark:text-amber-300">
                      {link.badge}
                    </span>
                  )}
                </NavLink>
              ))}
            </nav>
            <div className="mt-auto pt-6 border-t border-slate-200 dark:border-slate-700">
              {!user ? (
                <div className="flex flex-col gap-3">
                  <a
                    href="/login"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMobileMenuOpen(false);
                      handleLogin(e);
                    }}
                    className="w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 border border-slate-200 dark:border-slate-600 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                  >
                    Log In
                  </a>
                  <a
                    href="/signup"
                    onClick={(e) => {
                      e.preventDefault();
                      setIsMobileMenuOpen(false);
                      handleSignup(e);
                    }}
                    className="w-full text-center px-4 py-2.5 rounded-lg text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors"
                  >
                    Sign Up
                  </a>
                </div>
              ) : (
                <div>
                  <div className="px-1 py-2 mb-3">
                    <p className="text-sm font-semibold text-slate-900 dark:text-slate-100">
                      {user.displayName || 'Amazon Ads Operator'}
                    </p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate">
                      {user.email ?? 'No email on record'}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2">
                    <NavLink
                      to="/settings/profile"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      Profile Settings
                    </NavLink>
                    <NavLink
                      to="/settings/subscription"
                      onClick={() => setIsMobileMenuOpen(false)}
                      className="px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      Subscription
                    </NavLink>
                    <button
                      type="button"
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleLogout();
                      }}
                      className="w-full text-left px-4 py-2 rounded-lg text-sm font-medium text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700 transition-colors"
                    >
                      Log Out
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <SiteFooter
        onOpenPrivacy={() => openInfoPage('Privacy Policy', privacyPolicyContent)}
        onOpenTerms={() => openInfoPage('Terms of Service', termsOfServiceContent)}
        onOpenCookies={() => openInfoPage('Cookie Policy', cookiesContent)}
      />

      <Chatbot />

      {/* Vercel Analytics and Speed Insights */}
      <Analytics />
      <SpeedInsights />

      {/* Debug Mode Toggle */}
      <DebugModeToggle />

      {/* Keyboard Shortcuts Modal */}
      <KeyboardShortcutsModal
        isOpen={isShortcutsModalOpen}
        onClose={() => setIsShortcutsModalOpen(false)}
        shortcuts={shortcuts}
      />
    </div>
  );
}
